import { useMemo, useState } from "react";
import { Checkbox, Modal, Select, message } from "antd";
import { useSelector } from "react-redux";
import useReactIpLocation from "react-ip-details";
import dayjsTZ from "../../../../utils/dayjs";
import { useAuth } from "../../../../authentication/authHelpers";
import { editShiftsDayType } from "../../../punonjesit/EmployeeView/Payment/EmployeeShifts/utils/apiHelpers";
import { ModalXIcon, WarningIcon } from "../../../../assets/icons";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import { RichTextEditor } from "../../../commonComponents";
import "./BulkEditShiftModal.scss";

const { Option } = Select;

export const BULK_EDIT_LIMIT = 20;

function BulkEditShiftModal({ open, setOpen, shifts = [], refreshData }) {
  const auth = useAuth();
  const { ipResponse } = useReactIpLocation();

  const { programFields } = useSelector((state) => state.programFields);
  const { isDarkMode } = useSelector((state) => state.usersList);

  const [selectedIds, setSelectedIds] = useState(() =>
    shifts.map((el) => el?.clockingId).filter(Boolean),
  );
  const [dayType, setDayType] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);

  const dayTypeOptions = useMemo(
    () =>
      (
        (programFields || []).find((el) => el?.fieldName === "ClockIn Types") ||
        {}
      )?.fieldOptions || [],
    [programFields],
  );

  const dayTypeLabels = useMemo(() => {
    const toReturn = {};
    dayTypeOptions.forEach((el) => {
      toReturn[el?.name] = el?.description;
    });
    return toReturn;
  }, [dayTypeOptions]);

  const selectedShifts = useMemo(
    () => shifts.filter((el) => selectedIds.includes(el?.clockingId)),
    [shifts, selectedIds],
  );

  const allSelected = selectedIds.length === shifts.length;

  const toggleShift = (clockingId) => {
    setSelectedIds((prev) =>
      prev.includes(clockingId)
        ? prev.filter((el) => el !== clockingId)
        : [...prev, clockingId],
    );
  };

  const toggleAll = () => {
    setSelectedIds(
      allSelected ? [] : shifts.map((el) => el?.clockingId).filter(Boolean),
    );
  };

  //region HANDLE SAVE
  const handleSave = async () => {
    if (!dayType) {
      return message.error("Ju duhet të zgjidhni llojin e turnit!");
    }
    if (!selectedShifts.length) {
      return message.error("Ju duhet të zgjidhni të paktën një turn!");
    }
    if (selectedShifts.length > BULK_EDIT_LIMIT) {
      return message.error(
        `Ju nuk mund të ndryshoni më shumë se ${BULK_EDIT_LIMIT} turne njëherësh!`,
      );
    }

    setSaving(true);
    const adminLog = {
      createdAt: dayjsTZ().valueOf(),
      username: auth?.user?.given_name + " " + auth?.user?.family_name,
      userSub: auth?.user?.userSub,
      userRole: auth?.userRole,
      note,
      ipResponse: ipResponse || null,
    };

    try {
      const { ok, failed } = await editShiftsDayType(
        selectedShifts,
        { dayType },
        adminLog,
      );

      if (!!ok.length) {
        refreshData && refreshData(ok);
      }
      setResults({ ok, failed });

      if (!failed.length) {
        message.success(
          ok.length === 1
            ? "Turni u ndryshua me sukses!"
            : `${ok.length} turne u ndryshuan me sukses!`,
        );
      } else {
        message.warning(
          `${ok.length} turne u ndryshuan, ${failed.length} dështuan.`,
        );
      }
    } catch (err) {
      console.log("🚀 ~ handleSave ~ err:", err);
      message.error("Ka ndodhur një gabim gjatë ndryshimit të turneve.");
    } finally {
      setSaving(false);
    }
  };

  const failedIds = (results?.failed || []).map(
    ({ shift }) => shift?.clockingId,
  );
  const okIds = (results?.ok || []).map((el) => el?.clockingId);

  const rowStatus = (clockingId) => {
    if (failedIds.includes(clockingId)) return "failed";
    if (okIds.includes(clockingId)) return "ok";
    return "";
  };

  //region RETURN
  return (
    <Modal
      destroyOnClose={true}
      width={900}
      className={`bulk-edit-shift ${isDarkMode ? "darkHeader" : "lightHeader"}`}
      title={`Ndrysho llojin e ${shifts.length} turneve`}
      open={open}
      onCancel={() => setOpen(false)}
      centered={true}
      closeIcon={<ModalXIcon />}
      footer={[
        <MondayButton
          key={1}
          testid="mbyllFaqen"
          className="mondayButtonRed"
          onClick={() => setOpen(false)}
        >
          Mbyll Faqen
        </MondayButton>,
        <MondayButton
          key={2}
          testid="ruajNdryshimet"
          className="mondayButtonGreen"
          onClick={handleSave}
          spin={saving}
        >
          {`Ruaj Ndryshimet (${selectedShifts.length})`}
        </MondayButton>,
      ]}
    >
      <div className={`bulk-edit-wrapper ${isDarkMode ? "dark" : ""}`}>
        <div className="warning-title">
          <WarningIcon />
          <span>
            Ju jeni duke ndryshuar llojin e disa turneve njëherësh, siguroni të
            dhënat përpara se të ruani ndryshimet!
          </span>
        </div>

        <div className="bulk-field">
          <label>
            <strong style={{ color: "red" }}>*</strong>Lloji i turnit për të
            gjitha turnet e zgjedhura:
          </label>
          <Select
            className="daytype-select"
            showSearch
            data-testid="bulkDayType"
            popupClassName={isDarkMode ? "darkDropdown" : ""}
            value={dayType}
            onChange={setDayType}
            placeholder="Zgjidh nje opsion"
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {dayTypeOptions.map((el, idx) => (
              <Option key={`shiftType-${idx}`} value={el?.name}>
                {el?.description}
              </Option>
            ))}
          </Select>
        </div>

        <div className="shifts-picker">
          <div className="shifts-picker-header">
            <Checkbox checked={allSelected} onChange={toggleAll}>
              {`Turnet e zgjedhura (${selectedShifts.length}/${shifts.length})`}
            </Checkbox>
            {selectedShifts.length > BULK_EDIT_LIMIT ? (
              <span className="limit-warning">
                {`Maksimumi ${BULK_EDIT_LIMIT} turne njëherësh`}
              </span>
            ) : null}
          </div>
          <div className="shifts-list">
            {shifts.map((shift) => {
              const { clockingId, employeeFullName, data } = shift || {};
              return (
                <div
                  key={clockingId}
                  className={`shift-row ${rowStatus(clockingId)}`}
                >
                  <Checkbox
                    checked={selectedIds.includes(clockingId)}
                    onChange={() => toggleShift(clockingId)}
                  />
                  <span className="shift-employee">{employeeFullName}</span>
                  <span className="shift-date">
                    {dayjsTZ(data).format("DD/MM/YYYY")}
                  </span>
                  <span className="shift-hours">
                    {`${dayjsTZ(shift?.clockInDate).format("HH:mm")} - ${
                      !!shift?.clockOutDate
                        ? dayjsTZ(shift.clockOutDate).format("HH:mm")
                        : "--:--"
                    }`}
                  </span>
                  <span className="shift-daytype">
                    {dayTypeLabels[shift?.dayType] || shift?.dayType || "-"}
                    {!!dayType && dayType !== shift?.dayType ? (
                      <span className="shift-daytype-next">
                        {` → ${dayTypeLabels[dayType] || dayType}`}
                      </span>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="to-note">
          <label>Shënimi:</label>
          <RichTextEditor
            className={"to-note-textarea"}
            value={note}
            onChange={setNote}
          />
        </div>

        {!!results?.failed?.length ? (
          <div className="bulk-results">
            <span className="results-title">
              {`${results.failed.length} turne nuk u ndryshuan:`}
            </span>
            {results.failed.map(({ shift, error }) => (
              <span key={shift?.clockingId} className="results-row">
                {`${shift?.employeeFullName} | ${dayjsTZ(shift?.data).format(
                  "DD/MM/YYYY",
                )} — ${error?.message || "Gabim i panjohur"}`}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

export default BulkEditShiftModal;
